import format from 'date-fns/format';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Progress } from 'reactstrap';

import { COMPLETE, DUE, UPCOMING } from '../Constants/GatewayReminderInstances';
import { truncateValueFromString } from '../Lib/SignalUtils';

export function ReminderInstanceCalendarType(props) {
  const friendlyFormattedOverdueDisplay = useMemo(
      () => formatDistanceToNow(new Date(props.calendar_end)),
      [props.calendar_end]
    ),
    maintenanceDueFormatted = useMemo(
      () => format(new Date(props.calendar_end), 'LLLL do, y'),
      [props.calendar_end]
    ),
    status = useMemo(
      () =>
        props.isReportDisplay
          ? props.instance?.MaintenanceNotifications?.status
          : props.instance?.MaintenanceNotifications?.[0]?.status,
      [props.instance?.MaintenanceNotifications, props.isReportDisplay]
    );

  return (
    <>
      <Progress
        className="mb-1"
        color={props.progress_color}
        style={{ height: '0.5rem' }}
        value={props.calendar_percentage * 100}
      />
      <div className="d-flex align-items-center justify-content-between">
        {props.isCompletedOneTimeReminder ? (
          <small className={`three-quarter-text text-${props.text}`}>
            Complete
          </small>
        ) : !status || [COMPLETE, UPCOMING].includes(status) ? (
          <small className={`three-quarter-text text-${props.text}`}>
            Next upcoming on {props.calendar_upcoming}
          </small>
        ) : null}
        <small className={`three-quarter-text text-${props.text}`}>
          {(() => {
            if (props.isCompletedOneTimeReminder) return null;

            switch (status) {
              case DUE:
                return `${friendlyFormattedOverdueDisplay} overdue`;
              case UPCOMING:
              case COMPLETE:
              default:
                return `Next due on ${maintenanceDueFormatted}`;
            }
          })()}
        </small>
      </div>
    </>
  );
}

ReminderInstanceCalendarType.propTypes = {
  calendar_end: PropTypes.number,
  calendar_percentage: PropTypes.number,
  calendar_upcoming: PropTypes.string,
  instance: PropTypes.object,
  isCompletedOneTimeReminder: PropTypes.bool,
  isReportDisplay: PropTypes.bool,
  progress_color: PropTypes.string,
  text: PropTypes.string,
};

export function ReminderInstanceSignalType(props) {
  const completedAtValue = useMemo(
      () =>
        truncateValueFromString(
          props.signal_value_when_serviced,
          props.display_precision
        ),
      [props.display_precision, props.signal_value_when_serviced]
    ),
    currentValue = useMemo(
      () =>
        truncateValueFromString(props.signal_current, props.display_precision),
      [props.display_precision, props.signal_current]
    ),
    nextDueValue = useMemo(
      () =>
        truncateValueFromString(
          props.instance.next_signal_value_due,
          props.display_precision
        ),
      [props.display_precision, props.instance.next_signal_value_due]
    ),
    pastDueValue = useMemo(
      () =>
        truncateValueFromString(props.signal_current, props.display_precision) -
        props.signal_end,
      [props.display_precision, props.signal_current, props.signal_end]
    );

  return (
    <>
      <Progress
        className="my-1"
        color={props.progress_color}
        style={{ height: '0.5rem' }}
        value={props.signal_percentage * 100}
      />
      <div className="d-flex align-items-center justify-content-between">
        {(() => {
          switch (true) {
            case !props.isCompletedOneTimeReminder &&
              Number.isFinite(currentValue):
              return (
                <small className={`three-quarter-text text-${props.text}`}>
                  {currentValue} Total {props.unit ?? ''}
                </small>
              );
            case props.isCompletedOneTimeReminder &&
              Number.isFinite(completedAtValue):
              return (
                <small className={`three-quarter-text text-${props.text}`}>
                  Completed at {completedAtValue} {props.unit ?? ''}
                </small>
              );
            case !Number.isFinite(completedAtValue):
            case !Number.isFinite(currentValue):
              return (
                <small className="three-quarter-text text-danger">
                  Current signal value is unavailable.
                </small>
              );
            default:
              return null;
          }
        })()}
        {Number.isFinite(currentValue) ? (
          <small className={`three-quarter-text text-${props.text}`}>
            {(() => {
              if (props.isCompletedOneTimeReminder) return null;

              switch (props.instance?.MaintenanceNotifications?.[0]?.status) {
                case DUE:
                  return `${pastDueValue} ${props.unit ?? ''} overdue`;
                case UPCOMING:
                case COMPLETE:
                default:
                  return `Next due at ${nextDueValue} ${props.unit ?? ''}`;
              }
            })()}
          </small>
        ) : null}
      </div>
    </>
  );
}

ReminderInstanceSignalType.propTypes = {
  display_precision: PropTypes.number,
  instance: PropTypes.object,
  isCompletedOneTimeReminder: PropTypes.bool,
  isReportDisplay: PropTypes.bool,
  progress_color: PropTypes.string,
  signal_current: PropTypes.number,
  signal_end: PropTypes.number,
  signal_margin: PropTypes.number,
  signal_percentage: PropTypes.number,
  signal_value_when_serviced: PropTypes.string,
  text: PropTypes.string,
  unit: PropTypes.string,
};
ReminderInstanceSignalType.defaultProps = { display_precision: 2 };
